import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import Header from "../components/Header";
import breakpoints from "../components/breakpoints";
import axiosInstance, { getApiErrorMessage } from "../axiosInstance";

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;

  const apiBaseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "");
  if (!apiBaseUrl) return photoUrl;

  return `${apiBaseUrl}${photoUrl.startsWith("/") ? "" : "/"}${photoUrl}`;
};

const toBackground = (value) => {
  if (!value) return "rgba(255, 255, 255, 0.08)";
  if (/^(blob:|data:|https?:)/i.test(value)) {
    return `url(${value}) center / cover no-repeat`;
  }
  return value;
};

export default function GalleryDetail() {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let ignore = false;

    const fetchAlbum = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axiosInstance.get(`/photos/${galleryId}`);
        const post = response.data.data;
        const photoUrls = post.photoUrls || [];
        const photoIds = post.photoIds || [];

        if (
          photoUrls.length !== photoIds.length ||
          photoIds.some((photoId) => !Number.isInteger(photoId))
        ) {
          throw new Error("사진 정보가 올바르지 않습니다.");
        }

        const nextAlbum = {
          id: post.postId,
          title: post.title,
          category: post.category,
          photos: photoUrls.map((photoUrl, index) => ({
            photoId: photoIds[index],
            url: resolvePhotoUrl(photoUrl),
          })),
          content: post.content,
          date: post.eventDate.replaceAll("-", ". "),
          eventDate: post.eventDate,
          authorNickname: post.authorNickname,
          isOwner: post.isOwner,
        };

        if (!ignore) {
          setAlbum(nextAlbum);
          setSelectedPhoto(0);
        }
      } catch (requestError) {
        if (!ignore) {
          setAlbum(null);
          setError(
            requestError.response
              ? getApiErrorMessage(
                  requestError,
                  "게시글을 불러오지 못했습니다.",
                )
              : requestError.message || "게시글을 불러오지 못했습니다.",
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchAlbum();

    return () => {
      ignore = true;
    };
  }, [galleryId]);

  const photos = album?.photos || [];

  const handleEdit = () => {
    navigate(`/gallery/${album.id}/edit`, { state: { album } });
  };

  const handleDelete = () => {
    setDeleteError("");
    setDialog({ type: "delete", message: "정말 삭제하시겠습니까?" });
  };

  const confirmDelete = async () => {
    if (deleting || !album?.isOwner) return;

    setDeleting(true);
    setDeleteError("");

    try {
      await axiosInstance.delete(`/photos/${album.id}`);
      navigate("/gallery", { replace: true });
    } catch (requestError) {
      setDeleteError(
        getApiErrorMessage(
          requestError,
          "게시글을 삭제하지 못했습니다.",
        ),
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Header />
      <Page>
        <Content>
          {loading ? (
            <StateMessage>게시글을 불러오는 중...</StateMessage>
          ) : error ? (
            <StateMessage role="alert">{error}</StateMessage>
          ) : album ? (
            <>
              <PageTitle>{album.title}</PageTitle>

              <MetaRow>
                <MetaText>
                  {album.date}&nbsp;&nbsp;|&nbsp;&nbsp;{album.category}
                  &nbsp;&nbsp;|&nbsp;&nbsp;작성자 {album.authorNickname}
                </MetaText>
                {album.isOwner && (
                  <AuthorActions>
                    <EditButton type="button" onClick={handleEdit}>
                      <DesktopLabel>수정하기</DesktopLabel>
                      <MobileLabel>수정</MobileLabel>
                    </EditButton>
                    <DeleteButton type="button" onClick={handleDelete}>
                      <DesktopLabel>삭제하기</DesktopLabel>
                      <MobileLabel>삭제</MobileLabel>
                    </DeleteButton>
                  </AuthorActions>
                )}
              </MetaRow>

              {photos.length > 0 ? (
                <>
                  <MainPhoto
                    $background={toBackground(photos[selectedPhoto]?.url)}
                  />

                  <ThumbnailList aria-label="사진 선택">
                    {photos.map((photo, index) => (
                      <ThumbnailButton
                        key={photo.photoId}
                        type="button"
                        $active={selectedPhoto === index}
                        $background={toBackground(photo.url)}
                        aria-label={`${index + 1}번째 사진`}
                        aria-pressed={selectedPhoto === index}
                        onClick={() => setSelectedPhoto(index)}
                      />
                    ))}
                  </ThumbnailList>
                </>
              ) : (
                <PhotoEmptyState>등록된 사진이 없습니다.</PhotoEmptyState>
              )}

              {album.content && (
                <DescriptionBox>
                  <DescriptionTitle>내용</DescriptionTitle>
                  <Description>{album.content}</Description>
                </DescriptionBox>
              )}
            </>
          ) : null}
        </Content>
      </Page>

      {dialog && album && (
        <DialogOverlay onClick={() => !deleting && setDialog(null)}>
          <DialogBox role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <DialogMessage>{dialog.message}</DialogMessage>
            {deleteError && <DialogError role="alert">{deleteError}</DialogError>}
            <DialogButtons>
              <DialogSecondary
                type="button"
                disabled={deleting}
                onClick={() => setDialog(null)}
              >
                취소
              </DialogSecondary>
              <DialogPrimary
                type="button"
                disabled={deleting}
                onClick={confirmDelete}
              >
                {deleting ? "삭제 중..." : "삭제"}
              </DialogPrimary>
            </DialogButtons>
          </DialogBox>
        </DialogOverlay>
      )}
    </>
  );
}

const Page = styled.main`
  min-height: calc(100vh - 4.87rem);
  background: var(--gradient);
  color: var(--white);
`;

const Content = styled.div`
  width: min(1240px, calc(100% - 40px));
  margin: 0 auto;
  padding: 40px 0 72px;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.tablet}) {
    width: calc(100% - 32px);
    padding: 24px 0 60px;
  }
`;

const StateMessage = styled.div`
  display: flex;
  min-height: 640px;
  align-items: center;
  justify-content: center;
  color: rgba(249, 249, 249, 0.72);
  font-family: Pretendard, sans-serif;
  font-size: 16px;

  @media (max-width: ${breakpoints.tablet}) {
    min-height: 480px;
    font-size: 13px;
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 18px;
  font-family: Montserrat, Pretendard, sans-serif;
  font-size: 56px;
  font-weight: 700;
  line-height: 1.22;
  letter-spacing: -1.4px;
  text-align: center;

  @media (max-width: ${breakpoints.tablet}) {
    margin-bottom: 14px;
    font-size: 32px;
    letter-spacing: -0.8px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(1040px, 100%);
  min-height: 37px;
  margin: 0 auto 18px;

  @media (max-width: ${breakpoints.tablet}) {
    width: calc(100% - 32px);
    min-height: 31px;
    margin-bottom: 14px;
  }
`;

const MetaText = styled.p`
  margin: 0;
  color: rgba(249, 249, 249, 0.72);
  font-family: Pretendard, sans-serif;
  font-size: 11px;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 10px;
  }
`;

const AuthorActions = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: ${breakpoints.tablet}) {
    gap: 8px;
  }
`;

const AuthorButton = styled.button`
  min-height: 36px;
  padding: 9px 18px;
  border: 0;
  border-radius: 999px;
  color: var(--white);
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }

  @media (max-width: ${breakpoints.tablet}) {
    min-height: 31px;
    padding: 8px 14px;
  }
`;

const EditButton = styled(AuthorButton)`
  background: rgba(255, 255, 255, 0.14);
`;

const DeleteButton = styled(AuthorButton)`
  background: var(--orange);
`;

const DesktopLabel = styled.span`
  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

const MobileLabel = styled.span`
  display: none;

  @media (max-width: ${breakpoints.tablet}) {
    display: inline;
  }
`;

const MainPhoto = styled.div`
  width: min(900px, 100%);
  aspect-ratio: 15 / 8;
  margin: 0 auto;
  border-radius: 16px;
  background: ${({ $background }) => $background};

  @media (max-width: ${breakpoints.tablet}) {
    width: calc(100% - 32px);
    aspect-ratio: 326 / 260;
    border-radius: 12px;
  }
`;

const ThumbnailList = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  width: min(688px, 100%);
  margin: 12px auto 0;

  @media (max-width: ${breakpoints.tablet}) {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 9px;
    width: calc(100% - 32px);
    margin-top: 12px;
  }
`;

const ThumbnailButton = styled.button`
  width: 128px;
  height: 80px;
  padding: 0;
  border: 2px solid ${({ $active }) => ($active ? "var(--orange)" : "transparent")};
  border-radius: 10px;
  background: ${({ $background }) => $background};
  cursor: pointer;

  @media (max-width: ${breakpoints.tablet}) {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    border-radius: 9px;
  }
`;

const PhotoEmptyState = styled.div`
  display: flex;
  width: min(900px, 100%);
  aspect-ratio: 15 / 8;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(249, 249, 249, 0.68);
  font-family: Pretendard, sans-serif;
  font-size: 14px;

  @media (max-width: ${breakpoints.tablet}) {
    width: calc(100% - 32px);
    aspect-ratio: 326 / 260;
    border-radius: 12px;
    font-size: 12px;
  }
`;

const DescriptionBox = styled.section`
  width: min(900px, 100%);
  min-height: 101px;
  margin: 18px auto 0;
  padding: 20px 24px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  box-sizing: border-box;

  @media (max-width: ${breakpoints.tablet}) {
    width: calc(100% - 32px);
    min-height: 82px;
    margin-top: 14px;
    padding: 16px;
    border-radius: 10px;
  }
`;

const DescriptionTitle = styled.h2`
  margin: 0 0 12px;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 600;

  @media (max-width: ${breakpoints.tablet}) {
    margin-bottom: 10px;
    font-size: 12px;
  }
`;

const Description = styled.p`
  margin: 0;
  color: rgba(249, 249, 249, 0.82);
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  line-height: 1.55;

  @media (max-width: ${breakpoints.tablet}) {
    font-size: 10px;
  }
`;

const DialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.66);
  box-sizing: border-box;
`;

const DialogBox = styled.div`
  width: min(360px, 100%);
  padding: 28px;
  border-radius: 14px;
  background: #263b4e;
  color: var(--white);
  box-sizing: border-box;
`;

const DialogMessage = styled.p`
  margin: 0;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
`;

const DialogError = styled.p`
  margin: 14px 0 0;
  color: #ffb089;
  font-family: Pretendard, sans-serif;
  font-size: 11px;
  line-height: 1.4;
  text-align: center;
`;

const DialogButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
`;

const DialogButton = styled.button`
  min-width: 72px;
  padding: 9px 18px;
  border: 0;
  border-radius: 999px;
  color: var(--white);
  font-family: Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`;

const DialogSecondary = styled(DialogButton)`
  background: rgba(255, 255, 255, 0.14);
`;

const DialogPrimary = styled(DialogButton)`
  background: var(--orange);
`;
